"use client";

import { ArrowUpRight, ShieldCheck, Scale, Heart } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Policy() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    value: "10+ YRS",
    label: "Experience",
    icon: ShieldCheck
  }, {
    value: "100%",
    label: "Neutrality",
    icon: Scale
  }, {
    value: "Safe",
    label: "Environment",
    icon: Heart
  }];
  return <section data-section-id="5010" id="policyintroduction" ref={sectionRef} className="bg-[#E7D8C7] text-[#2F3631] relative overflow-hidden border-t border-[#8FA27C]/40">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row">
          {}
          <div className={`w-full lg:w-7/12 px-6 py-16 md:py-24 lg:py-32 border-r border-[#8FA27C]/40 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="font-mono text-[12px] tracking-[0.15em] uppercase mb-8 text-[#5E6A52]">
              01 / Professional Standards & Methodology
            </div>

            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-12 max-w-2xl">
              A Foundation of <span className="text-[#A3B18A]">Trust</span> and Clinical Precision.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col gap-3 p-6 bg-[#D9C2A6] border-l-4 border-[#8FA27C] rounded-lg">
                  <stat.icon className="size-5 text-[#5E6A52]" />
                  <div>
                    <div className="font-serif text-3xl leading-none mb-1">{stat.value}</div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-[#2F3631]/70">{stat.label}</div>
                  </div>
                </div>)}
            </div>

            <div className="prose prose-lg text-[#2F3631]/90 font-sans leading-relaxed max-w-2xl">
              <p className="mb-6">
                At Tiny to Teen Supervised Parenting & Education Services, we believe that supervised parenting time should be a safe, structured, and neutral experience for every child. Our methodology is rooted in trauma-informed care and child-centered principles, ensuring that families navigating separation or court involvement receive the highest level of professional support.
              </p>
              <p>
                We maintain firm boundaries while fostering a calm, compassionate environment where children can feel secure. Every interaction is documented with precision, providing the court and families with clear, objective observations. By prioritizing neutrality and safety, we help families move forward with clarity and confidence.
              </p>
            </div>

            <div className="mt-16">
              <Link to="/SafetyStandardsPage" className="group inline-flex items-center gap-4 px-8 py-4 bg-[#A3B18A] text-white font-mono text-xs tracking-widest uppercase transition-all duration-300 hover:bg-[#5E6A52] rounded-lg">
                View Our Standards
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="w-full lg:w-5/12 relative min-h-[400px] lg:min-h-full bg-[#D9C2A6]">
            <div className="absolute inset-0 overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/family_support_neutral_setting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/family_support_neutral_setting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/family_support_neutral_setting.png-wvc-sizes" alt="A calm, neutral, and professional space designed for family support services in Arizona" loading="lazy" decoding="async" className="h-full w-full object-cover mix-blend-multiply opacity-80 grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#E7D8C7]/40" />
            </div>

            {}
            <div className="absolute bottom-8 right-8 font-mono text-[10px] tracking-[0.2em] text-[#2F3631]/60 uppercase vertical-rl rotate-180">
              33.4484° N, 112.0740° W // ARIZONA BASED
            </div>

            <div className="absolute top-8 left-8 font-mono text-[10px] tracking-[0.2em] text-[#2F3631]/60 uppercase">
              EST. 2014 / NEUTRALITY FIRST
            </div>
          </div>
        </div>
      </div>
      
      {}
      <div className="h-px w-full bg-[#8FA27C]/40" />
    </section>;
}